import isObject from 'lodash/isObject';
import trim from 'lodash/trim';
import {setWindowLocation} from 'js-utils/http';
import {logCheckpointOnPageChange} from 'js-utils/janus';
import {
    getCanonicalSearchUrl,
    constants as destinationConstants,
} from '@eb/destination-utils';

import {getExperimentId} from '../../utils/feed';
import {
    getDateQuery,
} from '../../utils/search';

import {
    GA_HOME_FEED_CATEGORY,
    GA_HOME_CATEGORY,
    GA_RUN_SEARCH_ACTION,
    GA_RUN_SEARCH_HEADER_ACTION,
    GA_RUN_SEARCH_HEADER_RECENT,
    GA_RUN_SEARCH_HEADER_POPULAR,
    GA_RUN_MADLIB_SEARCH_ACTION,
} from '../../constants/analytics';
import {
    RUN_SEARCH_HEADER,
    UPDATE_SEARCH_SUGGESTIONS,
} from '../../constants/constants';
import {parseSuggestions} from '../../utils/suggestions';

import {
    searchAutocompleteSuggestions as searchAutocompleteSuggestionsApi,
} from './api';

const SEARCH_HEADER_ACTIONS = {
    [destinationConstants.SUGGESTION_TYPE_HISTORY]: GA_RUN_SEARCH_HEADER_RECENT,
    [destinationConstants.SUGGESTION_TYPE_POPULAR]: GA_RUN_SEARCH_HEADER_POPULAR,
};

const trackHeaderSearch = ({term, experimentId}) => ({
    type: 'RUN_SEARCH',
    meta: {
        analytics: {
            category: 'header',
            onWindowUnload: true,
            action: GA_RUN_SEARCH_ACTION,
            label: term,
            dimensions: {
                experimentId,
            },
        },
    },
});

const EMPTY_VALUE = 'empty';
const trackMadlibSearch = ({
    searchQuery,
    date = EMPTY_VALUE,
    tags = EMPTY_VALUE,
    loggedIn = true,
}) => ({
    type: 'RUN_MADLIBS_SEARCH',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            onWindowUnload: true,
            action: GA_RUN_MADLIB_SEARCH_ACTION,
            label: `${searchQuery} - ${date} - ${tags}`,
        },
    },
});

const trackSearchHeader = ({
    query,
    locationSlug,
    actionType,
    loggedIn = true,
}) => ({
    type: 'RUN_SEARCH_HEADER',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: SEARCH_HEADER_ACTIONS[actionType] || GA_RUN_SEARCH_HEADER_ACTION,
            onWindowUnload: true,
            label: `${locationSlug} | ${query}`,
        },
    },
});

export const UPDATE_SEARCH_LOCATION_SLUG = 'UPDATE_SEARCH_LOCATION_SLUG';

export const updateSearchLocationSlug = (slug) => ({
    type: UPDATE_SEARCH_LOCATION_SLUG,
    payload: slug,
});

export const runSearch = (term) => (
    (dispatch, getState) => {
        const state = getState();
        const {
            app: {
                domain,
                experiments,
            },
            location: {
                slug,
                bbox,
            },
        } = state;
        const experimentId = getExperimentId(experiments);
        const locationString = trim(slug) || 'local';

        const url = getCanonicalSearchUrl({
            slug: locationString,
            q: term,
            bbox,
        });

        logCheckpointOnPageChange(RUN_SEARCH_HEADER, domain)
            // Swallow errors since this shouldn't impact users at all
            .catch(() => null);

        dispatch(trackHeaderSearch({
            term,
            experimentId,
        }));

        setWindowLocation(url);
    }
);

export const searchMadLibsFilter = (tags, dates) => (
    (dispatch, getState) => {
        const {
            location: {slug, bbox},
            user: {isAuthenticated},
        } = getState();

        const dateQuery = getDateQuery(dates);
        const locationString = trim(slug) || 'local';
        const date = isObject(dates) ? null : dates;

        let url = getCanonicalSearchUrl({
            slug: locationString,
            dates: date,
            tags,
            bbox,
        });

        if (isObject(dates)) {
            url += dateQuery;
        }

        dispatch(trackMadlibSearch({
            searchQuery: slug,
            date,
            tags,
            loggedIn: isAuthenticated,
        }));

        setWindowLocation(url);
    }
);

const updateSearchAutocompleteSuggestions = (suggestions) => ({
    type: UPDATE_SEARCH_SUGGESTIONS,
    payload: suggestions,
});

export const searchAutocompleteSuggestions = (query) => (
    (dispatch, getState) => {
        if (!query) {
            return dispatch(updateSearchAutocompleteSuggestions({}));
        }

        const {
            location: {
                placeId,
                isOnline,
            },
        } = getState();

        return searchAutocompleteSuggestionsApi({placeId, onlineEventsOnly: isOnline, query})
            .then((response) => dispatch(updateSearchAutocompleteSuggestions(parseSuggestions(response))));
    }
);

export const searchFilterAction = (searchFilter) => (
    (dispatch, getState) => {
        const {
            query,
            locationSlug,
            actionType,
        } = searchFilter;

        const {
            user: {isAuthenticated},
            location: {bbox},
        } = getState();

        const locationString = trim(locationSlug) || 'local';

        const url = getCanonicalSearchUrl({
            slug: locationString,
            q: query,
            bbox,
        });

        dispatch(trackSearchHeader({
            query,
            locationSlug,
            actionType,
            loggedIn: isAuthenticated,
        }));

        setWindowLocation(url);
    }
);
